import { Band, BandStack } from '@/components/ui/Band'
import { ButtonLink } from '@/components/ui/Button'
import { PageHero } from '@/components/ui/PageHero'
import { Accordion } from '@/components/ui/Accordion'
import { SectionHeader } from '@/components/ui/Section'
import { useDocumentMeta } from '@/lib/seo'
import { ROUTES } from '@/lib/site'
import { ALL_FAQ_ITEMS, FAQ_GROUPS } from '@/content/faq'

const TOTAL_LAYERS = FAQ_GROUPS.length + 2

/**
 * FAQPage structured data, generated from the same questions rendered on the
 * page — so it can never describe content that is not actually here.
 */
function FaqStructuredData() {
  const payload = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: ALL_FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }

  return (
    <script
      type="application/ld+json"
      // Content is authored in this repository, not user input.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(payload) }}
    />
  )
}

export default function Faq() {
  useDocumentMeta(ROUTES.faq)

  return (
    <BandStack>
      <FaqStructuredData />

      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="faq">
        <PageHero
          crumb="FAQ"
          title="Questions, answered at the level they were asked."
          lede={'Where an answer is "not yet", it says not yet.'}
        />
      </Band>

      {FAQ_GROUPS.map((group, index) => (
        <Band
          key={group.id}
          layer={index + 2}
          total={TOTAL_LAYERS}
          tone={index % 2 === 0 ? 'warm' : 'mint'}
          id={group.id}
          labelledBy={`${group.id}-heading`}
        >
          <SectionHeader
            id={`${group.id}-heading`}
            eyebrow={<span className="text-primary">{group.eyebrow}</span>}
            title={group.heading}
          />
          <div className="mt-10">
            <Accordion items={group.items} />
          </div>
        </Band>
      ))}

      <Band
        layer={TOTAL_LAYERS}
        total={TOTAL_LAYERS}
        tone="ice"
        labelledBy="faq-cta"
        showMark={false}
      >
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="faq-cta"
            className="text-balance text-[26px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
          >
            Something not answered here?
          </h2>
          <p className="mx-auto mt-5 max-w-[56ch] text-pretty text-[15px] leading-[1.75] text-muted">
            Ask it directly. If the answer is &ldquo;we do not know yet&rdquo;, that is what you
            will get.
          </p>
          <div className="mt-9 flex justify-center">
            <ButtonLink to={ROUTES.contact.path} size="lg">
              Ask a question
            </ButtonLink>
          </div>
        </div>
      </Band>
    </BandStack>
  )
}
